'use strict';

import { logInfo, logWarn, logError } from '../core/bambuserLogger';
import { trimString } from '../core/bambuserFormatters';
import { httpGetJson } from '../core/bambuserApiClient';
import {
    bambuserProductIdToPid,
    bambuserReferenceToPid,
    pendingHydrationByProductId,
} from '../core/bambuserSharedState';
import {
    extractPid,
    getRawProductReference,
    isColorGroupPid,
    getBasePidFromColorPid,
    getHydrationPidCandidates,
    rememberResolvedPidFromEvent,
} from './bambuserPidResolver';
import {
    getProductName,
    getProductBrand,
    getProductDescription,
    getProductLongDescription,
    getProductImageUrls,
    getProductPriceData,
    isBuyablePrice,
    getProductSizeOptions,
    getPreferredCartPid,
    getProductUrlData,
    getAllColorValues,
    isColorValueAvailable,
    buildColorProduct,
} from './bambuserProductMapper';

// ---------------------------------------------------------------------------
// Hydration endpoint builder
// ---------------------------------------------------------------------------

export function buildHydrationEndpoints (config, pidCandidates) {
    var endpoints = [];
    var seen = {};
    var candidates = Array.isArray(pidCandidates) ? pidCandidates : [];
    var hasPrimaryEndpoint = !!(config && config.productDataUrl);

    if (!hasPrimaryEndpoint || !candidates.length) {
        return endpoints;
    }

    function pushEndpoint (baseUrl, source, requestPid) {
        if (!baseUrl || !requestPid) {
            return;
        }

        var url = baseUrl + '?pid=' + encodeURIComponent(requestPid);
        if (seen[url]) {
            return;
        }

        seen[url] = true;
        endpoints.push({ url: url, source: source, requestPid: requestPid });
    }

    for (var i = 0; i < candidates.length; i++) {
        var pid = candidates[i];

        pushEndpoint(config.productDataUrl, 'primary', pid);
    }

    return endpoints;
}

// ---------------------------------------------------------------------------
// PID resolution for add-to-cart
// ---------------------------------------------------------------------------

export function resolveAddToCartPid (pid, event, config) {
    var initialPid = trimString(pid);

    if (!initialPid || !config || !isColorGroupPid(initialPid)) {
        return Promise.resolve(initialPid);
    }

    var endpoints = buildHydrationEndpoints(
        config,
        getHydrationPidCandidates(initialPid),
    );
    if (!endpoints.length) {
        return Promise.resolve(initialPid);
    }

    function tryResolveAt (index) {
        if (index >= endpoints.length) {
            return Promise.resolve(initialPid);
        }

        var endpoint = endpoints[index];
        return httpGetJson(endpoint.url)
            .then(function (payload) {
                var fullProduct = payload && payload.fullProduct;
                if (!fullProduct) {
                    return tryResolveAt(index + 1);
                }

                var inStock = fullProduct.available !== false;
                var sizeOptions = getProductSizeOptions(
                    fullProduct,
                    endpoint.requestPid || initialPid,
                    inStock,
                );
                var resolvedPid = getPreferredCartPid(
                    sizeOptions,
                    endpoint.requestPid || initialPid,
                );

                if (!resolvedPid || resolvedPid === initialPid) {
                    return tryResolveAt(index + 1);
                }

                rememberResolvedPidFromEvent(event, resolvedPid);
                return resolvedPid;
            })
            .catch(function () {
                return tryResolveAt(index + 1);
            });
    }

    return tryResolveAt(0);
}

// ---------------------------------------------------------------------------
// Update player product (multi-variation)
// ---------------------------------------------------------------------------

function buildVariation (variationFactory, varData, fallbackPriceData, config) {
    var varProduct = varData.fullProduct;
    var varPid = varData.pid;
    var varInStock = varData.inStock;
    var varImageUrls = getProductImageUrls(varProduct);
    var varSizeOptions = getProductSizeOptions(varProduct, varPid, varInStock);
    var varPriceData = getProductPriceData(varProduct);
    var varCurrentPrice = varPriceData.current || (fallbackPriceData && fallbackPriceData.current);
    var varOriginalPrice = varPriceData.original || (fallbackPriceData && fallbackPriceData.original);
    var varHasPrice = isBuyablePrice(varCurrentPrice);

    var varColorNameRaw = varData.colorValue
        ? (trimString(varData.colorValue.displayValue) || trimString(varData.colorValue.value) || 'Default')
        : 'Default';
    var varColorName = varColorNameRaw
        .toLowerCase()
        .replace(/(^|\s|-)\p{L}/gu, function (c) { return c.toUpperCase(); });
    var varHexCode = varData.colorValue
        ? trimString(varData.colorValue.colorHexCode || varData.colorValue.hex || varData.colorValue.hexCode)
        : null;

    var variation = variationFactory()
        .name(varColorName)
        .sku(varPid)
        .imageUrls(varImageUrls)
        .sizes(function (sizeFactory) {
            var sizes = [];

            for (var s = 0; s < varSizeOptions.length; s++) {
                var sizeOption = varSizeOptions[s];
                var size = sizeFactory()
                    .name(sizeOption.name)
                    .sku(sizeOption.sku)
                    .inStock(varInStock ? sizeOption.inStock : false);

                if (varHasPrice) {
                    size = size.price(function (priceFactory) {
                        var price = priceFactory.current(varCurrentPrice);
                        if (varOriginalPrice && varOriginalPrice > varCurrentPrice) {
                            price = price.original(varOriginalPrice);
                        }
                        if (config && config.currency) {
                            price = price.currency(config.currency);
                        }
                        return price;
                    });
                }

                sizes.push(size);
            }

            return sizes;
        });

    variation = variation.attributes(function (attributeFactory) {
        var attributes = attributeFactory.colorName(varColorName);
        if (varHexCode) {
            attributes = attributes.colorHexCode(varHexCode);
        }
        return attributes;
    });

    return variation;
}

export function updatePlayerProduct (
    player,
    bambuserProductId,
    masterPid,
    variations,
    config,
) {
    if (!player || !bambuserProductId || !masterPid || !variations || !variations.length) {
        logWarn('updatePlayerProduct skipped: missing required data');
        return;
    }

    var firstVariation = variations[0];
    var productName = getProductName(firstVariation.fullProduct);
    var brandName = getProductBrand(firstVariation.fullProduct);
    var description = getProductDescription(firstVariation.fullProduct);
    var longDescription = getProductLongDescription(firstVariation.fullProduct);
    var fallbackPriceData = getProductPriceData(firstVariation.fullProduct);
    var productUrl = getProductUrlData(firstVariation.fullProduct, masterPid).url;

    // Set default cart PID to first available variation's preferred PID
    var defaultCartPid = masterPid;
    for (var v = 0; v < variations.length; v++) {
        if (variations[v].inStock) {
            var sizeOpts = getProductSizeOptions(variations[v].fullProduct, variations[v].pid, true);
            var preferred = getPreferredCartPid(sizeOpts, variations[v].pid);
            if (preferred) {
                defaultCartPid = preferred;
                break;
            }
        }
    }
    bambuserProductIdToPid[bambuserProductId] = defaultCartPid;

    // Default variation index: first available color
    var defaultVariationIndex = 0;
    for (var d = 0; d < variations.length; d++) {
        if (variations[d].inStock) {
            defaultVariationIndex = d;
            break;
        }
    }

    logInfo('hydrating player product', {
        bambuserProductId: bambuserProductId,
        masterPid: masterPid,
        variationsCount: variations.length,
        name: productName,
    });

    player.updateProduct(bambuserProductId, function (productFactory) {
        var configuredProduct = productFactory.inheritFromPlaceholder();

        if (productUrl) {
            configuredProduct = configuredProduct.publicUrl(productUrl);
        }

        return configuredProduct.product(function (productDetailFactory) {
            var productDetail = productDetailFactory
                .name(productName)
                .sku(masterPid)
                .defaultVariationIndex(defaultVariationIndex)
                .variations(function (variationFactory) {
                    var bambuserVariations = [];

                    for (var i = 0; i < variations.length; i++) {
                        bambuserVariations.push(
                            buildVariation(variationFactory, variations[i], fallbackPriceData, config)
                        );
                    }

                    return bambuserVariations;
                });

            if (brandName) {
                productDetail = productDetail.brandName(brandName);
            }

            if (description) {
                productDetail = productDetail.introduction(description);
            }

            if (longDescription) {
                productDetail = productDetail.description(longDescription);
            }

            return productDetail;
        });
    });
}

// ---------------------------------------------------------------------------
// Hydrate a single product for the player (multi-color)
// ---------------------------------------------------------------------------

export function hydrateProductForPlayer (player, product, config) {
    var hasEndpoint = !!(config && config.productDataUrl);

    if (
        !product ||
        !product.id ||
        !config ||
        !hasEndpoint
    ) {
        logWarn('hydrate skipped: missing product or config');
        return;
    }

    var pid = extractPid(product);
    var rawReference = getRawProductReference(product);
    if (!pid) {
        logWarn('hydrate skipped: unable to resolve pid from product', product);
        return;
    }

    if (pendingHydrationByProductId[product.id]) {
        logInfo('hydrate skipped: product already hydrating', { productId: product.id });
        return;
    }

    // Always resolve to master PID for full-color hydration
    var masterPid = getBasePidFromColorPid(pid) || pid;

    // Register a pending promise so ADD_TO_CART can defer until hydration settles
    var hydrateSettled;
    pendingHydrationByProductId[product.id] = new Promise(function (resolve) {
        hydrateSettled = resolve;
    });

    // Single call: fetch master product with per-color prices
    var endpointUrl = config.productDataUrl;
    var masterUrl = endpointUrl
        + '?pid=' + encodeURIComponent(masterPid)
        + '&includeVariationPrices=true';

    httpGetJson(masterUrl)
        .then(function (payload) {
            var masterProduct = payload && payload.fullProduct;
            if (!masterProduct) {
                logWarn('hydrate: master product not found', { pid: masterPid });
                return null;
            }

            var colorValues = getAllColorValues(masterProduct);

            // No color attribute → single-variation product
            if (!colorValues.length) {
                var singleVariation = [{
                    fullProduct: masterProduct,
                    pid: masterPid,
                    colorValue: null,
                    inStock: masterProduct.available !== false,
                }];
                updatePlayerProduct(player, product.id, masterPid, singleVariation, config);
                return null;
            }

            // Build all color variations from the single master response
            var variations = [];
            for (var i = 0; i < colorValues.length; i++) {
                var colorValue = colorValues[i];
                var colorCode = trimString(colorValue.value || colorValue.id);
                if (!colorCode) { continue; }

                var colorPid = masterPid + '_color_' + colorCode;
                var colorInStock = isColorValueAvailable(colorValue);
                var colorProduct = buildColorProduct(masterProduct, colorValue);

                variations.push({
                    fullProduct: colorProduct,
                    pid: colorPid,
                    colorValue: colorValue,
                    inStock: colorInStock,
                });
            }

            if (!variations.length) {
                logWarn('hydrate: no valid color variations, using master data', { pid: masterPid });
                variations = [{
                    fullProduct: masterProduct,
                    pid: masterPid,
                    colorValue: colorValues[0] || null,
                    inStock: masterProduct.available !== false,
                }];
            }

            updatePlayerProduct(player, product.id, masterPid, variations, config);
            return null;
        })
        .catch(function (error) {
            logError('hydrate failed', {
                bambuserProductId: product.id,
                pid: pid,
                message: error && error.message ? error.message : error,
            });
        })
        .then(
            function () {
                if (rawReference) {
                    bambuserReferenceToPid[rawReference] =
                        bambuserProductIdToPid[product.id] || masterPid;
                }
                delete pendingHydrationByProductId[product.id];
                hydrateSettled();
            },
            function () {
                delete pendingHydrationByProductId[product.id];
                hydrateSettled();
            },
        );
}
